<template>
    <div class="min-h-screen bg-white">
        <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div class="text-center mb-16">
                <h1 class="text-4xl md:text-6xl font-marketing font-bold text-primary mb-6">
                    Apply for {{ careerTitle || 'Position' }}
                </h1>
                <p class="text-xl text-gray-700 max-w-3xl mx-auto font-sans leading-relaxed">
                    We are excited to learn more about you. Please fill out the form below to submit your application.
                </p>
            </div>

            <div v-if="submissionSuccess" class="bg-green-50 border border-green-200 rounded-lg p-8 mb-8">
                <div class="flex items-start">
                    <svg class="w-8 h-8 text-green-600 mr-4 flex-shrink-0 mt-1" fill="none" stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                        <h2 class="text-2xl font-marketing font-bold text-green-800 mb-2">
                            Application Submitted Successfully!
                        </h2>
                        <p class="text-gray-700 font-sans leading-relaxed">
                            Thank you for your interest. Our team will review your application and get back to you soon.
                        </p>
                    </div>
                </div>
            </div>

            <div v-else class="bg-white rounded-lg shadow-sm p-8 md:p-12">
                <form @submit.prevent="handleSubmit" class="space-y-8">
                    <!-- Resume and Additional Material Section -->
                    <div class="space-y-4">
                        <h3 class="text-lg font-marketing font-bold text-primary">Resume and Additional Material</h3>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label for="resumeFile"
                                    class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    Resume/CV <span class="text-red-500">*</span>
                                </label>
                                <div class="file-drop-zone" :class="{ 'drag-over': isDraggingResume }"
                                    @drop.prevent="handleResumeDrop" @dragover.prevent="isDraggingResume = true"
                                    @dragleave.prevent="isDraggingResume = false" @click="triggerResumeInput">
                                    <input ref="resumeInputRef" type="file" id="resumeFile" @change="handleResumeSelect"
                                        accept=".pdf,.doc,.docx" required class="hidden" />
                                    <div v-if="form.resumeFileName" class="file-selected-content">
                                        <div
                                            class="w-16 h-16 rounded-full bg-[#2D4D31] flex items-center justify-center mb-3">
                                            <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p class="text-sm font-sans text-[#111111] mb-2 break-words max-w-full">
                                            {{ form.resumeFileName.length > 30 ? form.resumeFileName.substring(0, 30) +
                                                '...' : form.resumeFileName }}
                                        </p>
                                        <button type="button" @click.stop="removeResume"
                                            class="text-sm font-sans text-[#7C898B] uppercase hover:text-[#253439] transition-colors">
                                            REMOVE
                                        </button>
                                    </div>
                                    <div v-else class="file-drop-content">
                                        <svg class="w-16 h-16 text-[#DDC1A7] mb-4" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                        </svg>
                                        <p class="text-sm font-sans text-[#111111] mb-1">Drop Resume Here</p>
                                        <p class="text-xs font-sans text-[#111111] mb-2">or</p>
                                        <p class="text-sm font-sans font-bold text-[#B29E84]">Upload Resume</p>
                                    </div>
                                    <p v-if="uploadingResume"
                                        class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 rounded-xl text-sm text-blue-600 font-sans">
                                        Uploading resume...
                                    </p>
                                </div>
                                <p class="text-xs text-gray-500 mt-1 font-sans">
                                    Accepted formats: PDF, DOC, DOCX (Max 10MB)
                                </p>
                            </div>

                            <div>
                                <label for="coverLetterFile"
                                    class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    Cover Letter (Optional)
                                </label>
                                <div class="file-drop-zone" :class="{ 'drag-over': isDraggingCoverLetter }"
                                    @drop.prevent="handleCoverLetterDrop"
                                    @dragover.prevent="isDraggingCoverLetter = true"
                                    @dragleave.prevent="isDraggingCoverLetter = false" @click="triggerCoverLetterInput">
                                    <input ref="coverLetterInputRef" type="file" id="coverLetterFile"
                                        @change="handleCoverLetterSelect" accept=".pdf,.doc,.docx" class="hidden" />
                                    <div v-if="form.coverLetterFileName" class="file-selected-content">
                                        <div
                                            class="w-16 h-16 rounded-full bg-[#2D4D31] flex items-center justify-center mb-3">
                                            <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p class="text-sm font-sans text-[#111111] mb-2 break-words max-w-full">
                                            {{ form.coverLetterFileName.length > 30 ?
                                                form.coverLetterFileName.substring(0, 30) + '...' : form.coverLetterFileName
                                            }}
                                        </p>
                                        <button type="button" @click.stop="removeCoverLetter"
                                            class="text-sm font-sans text-[#7C898B] uppercase hover:text-[#253439] transition-colors">
                                            REMOVE
                                        </button>
                                    </div>
                                    <div v-else class="file-drop-content">
                                        <svg class="w-16 h-16 text-[#DDC1A7] mb-4" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                        </svg>
                                        <p class="text-sm font-sans text-[#111111] mb-1">Drop Cover Letter Here</p>
                                        <p class="text-xs font-sans text-[#111111] mb-2">or</p>
                                        <p class="text-sm font-sans font-bold text-[#B29E84]">Upload Cover Letter</p>
                                    </div>
                                    <p v-if="uploadingCoverLetter"
                                        class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 rounded-xl text-sm text-blue-600 font-sans">
                                        Uploading cover letter...
                                    </p>
                                </div>
                                <p class="text-xs text-gray-500 mt-1 font-sans">
                                    Accepted formats: PDF, DOC, DOCX (Max 10MB)
                                </p>
                            </div>
                        </div>

                        <div>
                            <label class="block text-sm font-marketing font-semibold text-primary mb-2">
                                Additional Links (Optional)
                            </label>
                            <div class="space-y-3">
                                <div v-for="(link, index) in form.additionalLinks" :key="index" class="flex gap-2">
                                    <input type="url" v-model="link.url"
                                        :placeholder="link.label || 'https://example.com'"
                                        class="flex-1 px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
                                    <input type="text" v-model="link.label" placeholder="Label (e.g., LinkedIn)"
                                        class="w-32 px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
                                    <button type="button" @click="removeLink(index)"
                                        class="px-4 py-3 bg-red-50 text-red-600 border border-red-300 rounded-md hover:bg-red-100 transition-colors font-sans">
                                        Remove
                                    </button>
                                </div>
                                <button type="button" @click="addLink"
                                    class="px-4 py-2 bg-green-50 text-green-700 border border-green-300 rounded-md hover:bg-green-100 transition-colors font-sans font-semibold">
                                    + Add Link
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Personal Details Section -->
                    <div class="space-y-4">
                        <h3 class="text-xl font-marketing font-bold text-primary">Personal Details</h3>

                        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label for="firstName"
                                    class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    First Name <span class="text-red-500">*</span>
                                </label>
                                <input type="text" id="firstName" v-model="form.firstName" required
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                                    placeholder="John" />
                            </div>

                            <div>
                                <label for="middleName"
                                    class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    Middle Name
                                </label>
                                <input type="text" id="middleName" v-model="form.middleName"
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                                    placeholder="Optional" />
                            </div>

                            <div>
                                <label for="lastName"
                                    class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    Last Name <span class="text-red-500">*</span>
                                </label>
                                <input type="text" id="lastName" v-model="form.lastName" required
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                                    placeholder="Doe" />
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label for="email" class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    Email Address <span class="text-red-500">*</span>
                                </label>
                                <input type="email" id="email" v-model="form.email" required
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                                    placeholder="your.email@example.com" />
                            </div>

                            <div>
                                <label for="phone" class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    Phone Number <span class="text-red-500">*</span>
                                </label>
                                <input type="tel" id="phone" v-model="form.phone" @blur="validatePhone" required
                                    class="w-full px-4 py-3 bg-white text-black border rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                                    :class="phoneError ? 'border-red-500' : 'border-gray-300'"
                                    placeholder="+1 (555) 123-4567" />
                                <p v-if="phoneError" class="text-xs text-red-600 mt-1 font-sans">{{ phoneError }}</p>
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label for="pronouns"
                                    class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    Pronouns
                                </label>
                                <select id="pronouns" v-model="form.pronouns"
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans">
                                    <option value="">Select pronouns</option>
                                    <option value="she/her/hers">she, her, hers</option>
                                    <option value="he/him/his">he, him, his</option>
                                    <option value="they/them/theirs">they, them, theirs</option>
                                    <option value="self-describe">Self-describe</option>
                                </select>
                            </div>

                            <div v-if="form.pronouns === 'self-describe'">
                                <label for="pronounsCustom"
                                    class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    Please Specify Pronouns
                                </label>
                                <input type="text" id="pronounsCustom" v-model="form.pronounsCustom"
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                                    placeholder="Enter your pronouns" />
                            </div>
                        </div>
                    </div>

                    <!-- Address Subsection -->
                    <div class="space-y-4">
                        <h3 class="text-lg font-marketing font-bold text-primary">Address</h3>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label for="country"
                                    class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    Country <span class="text-red-500">*</span>
                                </label>
                                <select id="country" v-model="form.country" required
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans">
                                    <option value="">Select a country</option>
                                    <option value="Afghanistan">Afghanistan</option>
                                    <option value="Albania">Albania</option>
                                    <option value="Algeria">Algeria</option>
                                    <option value="Andorra">Andorra</option>
                                    <option value="Angola">Angola</option>
                                    <option value="Antigua and Barbuda">Antigua and Barbuda</option>
                                    <option value="Argentina">Argentina</option>
                                    <option value="Armenia">Armenia</option>
                                    <option value="Australia">Australia</option>
                                    <option value="Austria">Austria</option>
                                    <option value="Azerbaijan">Azerbaijan</option>
                                    <option value="Bahamas">Bahamas</option>
                                    <option value="Bahrain">Bahrain</option>
                                    <option value="Bangladesh">Bangladesh</option>
                                    <option value="Barbados">Barbados</option>
                                    <option value="Belarus">Belarus</option>
                                    <option value="Belgium">Belgium</option>
                                    <option value="Belize">Belize</option>
                                    <option value="Benin">Benin</option>
                                    <option value="Bhutan">Bhutan</option>
                                    <option value="Bolivia">Bolivia</option>
                                    <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
                                    <option value="Botswana">Botswana</option>
                                    <option value="Brazil">Brazil</option>
                                    <option value="Brunei">Brunei</option>
                                    <option value="Bulgaria">Bulgaria</option>
                                    <option value="Burkina Faso">Burkina Faso</option>
                                    <option value="Burundi">Burundi</option>
                                    <option value="Cabo Verde">Cabo Verde</option>
                                    <option value="Cambodia">Cambodia</option>
                                    <option value="Cameroon">Cameroon</option>
                                    <option value="Canada">Canada</option>
                                    <option value="Central African Republic">Central African Republic</option>
                                    <option value="Chad">Chad</option>
                                    <option value="Chile">Chile</option>
                                    <option value="China">China</option>
                                    <option value="Colombia">Colombia</option>
                                    <option value="Comoros">Comoros</option>
                                    <option value="Congo (Congo-Brazzaville)">Congo (Congo-Brazzaville)</option>
                                    <option value="Costa Rica">Costa Rica</option>
                                    <option value="Croatia">Croatia</option>
                                    <option value="Cuba">Cuba</option>
                                    <option value="Cyprus">Cyprus</option>
                                    <option value="Czech Republic">Czech Republic</option>
                                    <option value="Democratic Republic of the Congo">Democratic Republic of the Congo
                                    </option>
                                    <option value="Denmark">Denmark</option>
                                    <option value="Djibouti">Djibouti</option>
                                    <option value="Dominica">Dominica</option>
                                    <option value="Dominican Republic">Dominican Republic</option>
                                    <option value="Ecuador">Ecuador</option>
                                    <option value="Egypt">Egypt</option>
                                    <option value="El Salvador">El Salvador</option>
                                    <option value="Equatorial Guinea">Equatorial Guinea</option>
                                    <option value="Eritrea">Eritrea</option>
                                    <option value="Estonia">Estonia</option>
                                    <option value="Eswatini">Eswatini</option>
                                    <option value="Ethiopia">Ethiopia</option>
                                    <option value="Fiji">Fiji</option>
                                    <option value="Finland">Finland</option>
                                    <option value="France">France</option>
                                    <option value="Gabon">Gabon</option>
                                    <option value="Gambia">Gambia</option>
                                    <option value="Georgia">Georgia</option>
                                    <option value="Germany">Germany</option>
                                    <option value="Ghana">Ghana</option>
                                    <option value="Greece">Greece</option>
                                    <option value="Grenada">Grenada</option>
                                    <option value="Guatemala">Guatemala</option>
                                    <option value="Guinea">Guinea</option>
                                    <option value="Guinea-Bissau">Guinea-Bissau</option>
                                    <option value="Guyana">Guyana</option>
                                    <option value="Haiti">Haiti</option>
                                    <option value="Holy See">Holy See</option>
                                    <option value="Honduras">Honduras</option>
                                    <option value="Hungary">Hungary</option>
                                    <option value="Iceland">Iceland</option>
                                    <option value="India">India</option>
                                    <option value="Indonesia">Indonesia</option>
                                    <option value="Iran">Iran</option>
                                    <option value="Iraq">Iraq</option>
                                    <option value="Ireland">Ireland</option>
                                    <option value="Israel">Israel</option>
                                    <option value="Italy">Italy</option>
                                    <option value="Jamaica">Jamaica</option>
                                    <option value="Japan">Japan</option>
                                    <option value="Jordan">Jordan</option>
                                    <option value="Kazakhstan">Kazakhstan</option>
                                    <option value="Kenya">Kenya</option>
                                    <option value="Kiribati">Kiribati</option>
                                    <option value="Kuwait">Kuwait</option>
                                    <option value="Kyrgyzstan">Kyrgyzstan</option>
                                    <option value="Laos">Laos</option>
                                    <option value="Latvia">Latvia</option>
                                    <option value="Lebanon">Lebanon</option>
                                    <option value="Lesotho">Lesotho</option>
                                    <option value="Liberia">Liberia</option>
                                    <option value="Libya">Libya</option>
                                    <option value="Liechtenstein">Liechtenstein</option>
                                    <option value="Lithuania">Lithuania</option>
                                    <option value="Luxembourg">Luxembourg</option>
                                    <option value="Madagascar">Madagascar</option>
                                    <option value="Malawi">Malawi</option>
                                    <option value="Malaysia">Malaysia</option>
                                    <option value="Maldives">Maldives</option>
                                    <option value="Mali">Mali</option>
                                    <option value="Malta">Malta</option>
                                    <option value="Marshall Islands">Marshall Islands</option>
                                    <option value="Mauritania">Mauritania</option>
                                    <option value="Mauritius">Mauritius</option>
                                    <option value="Mexico">Mexico</option>
                                    <option value="Micronesia">Micronesia</option>
                                    <option value="Moldova">Moldova</option>
                                    <option value="Monaco">Monaco</option>
                                    <option value="Mongolia">Mongolia</option>
                                    <option value="Montenegro">Montenegro</option>
                                    <option value="Morocco">Morocco</option>
                                    <option value="Mozambique">Mozambique</option>
                                    <option value="Myanmar">Myanmar</option>
                                    <option value="Namibia">Namibia</option>
                                    <option value="Nauru">Nauru</option>
                                    <option value="Nepal">Nepal</option>
                                    <option value="Netherlands">Netherlands</option>
                                    <option value="New Zealand">New Zealand</option>
                                    <option value="Nicaragua">Nicaragua</option>
                                    <option value="Niger">Niger</option>
                                    <option value="Nigeria">Nigeria</option>
                                    <option value="North Korea">North Korea</option>
                                    <option value="North Macedonia">North Macedonia</option>
                                    <option value="Norway">Norway</option>
                                    <option value="Oman">Oman</option>
                                    <option value="Pakistan">Pakistan</option>
                                    <option value="Palau">Palau</option>
                                    <option value="Palestine State">Palestine State</option>
                                    <option value="Panama">Panama</option>
                                    <option value="Papua New Guinea">Papua New Guinea</option>
                                    <option value="Paraguay">Paraguay</option>
                                    <option value="Peru">Peru</option>
                                    <option value="Philippines">Philippines</option>
                                    <option value="Poland">Poland</option>
                                    <option value="Portugal">Portugal</option>
                                    <option value="Qatar">Qatar</option>
                                    <option value="Romania">Romania</option>
                                    <option value="Russia">Russia</option>
                                    <option value="Rwanda">Rwanda</option>
                                    <option value="Saint Kitts and Nevis">Saint Kitts and Nevis</option>
                                    <option value="Saint Lucia">Saint Lucia</option>
                                    <option value="Saint Vincent and the Grenadines">Saint Vincent and the Grenadines
                                    </option>
                                    <option value="Samoa">Samoa</option>
                                    <option value="San Marino">San Marino</option>
                                    <option value="Sao Tome and Principe">Sao Tome and Principe</option>
                                    <option value="Saudi Arabia">Saudi Arabia</option>
                                    <option value="Senegal">Senegal</option>
                                    <option value="Serbia">Serbia</option>
                                    <option value="Seychelles">Seychelles</option>
                                    <option value="Sierra Leone">Sierra Leone</option>
                                    <option value="Singapore">Singapore</option>
                                    <option value="Slovakia">Slovakia</option>
                                    <option value="Slovenia">Slovenia</option>
                                    <option value="Solomon Islands">Solomon Islands</option>
                                    <option value="Somalia">Somalia</option>
                                    <option value="South Africa">South Africa</option>
                                    <option value="South Korea">South Korea</option>
                                    <option value="South Sudan">South Sudan</option>
                                    <option value="Spain">Spain</option>
                                    <option value="Sri Lanka">Sri Lanka</option>
                                    <option value="Sudan">Sudan</option>
                                    <option value="Suriname">Suriname</option>
                                    <option value="Sweden">Sweden</option>
                                    <option value="Switzerland">Switzerland</option>
                                    <option value="Syria">Syria</option>
                                    <option value="Tajikistan">Tajikistan</option>
                                    <option value="Tanzania">Tanzania</option>
                                    <option value="Thailand">Thailand</option>
                                    <option value="Timor-Leste">Timor-Leste</option>
                                    <option value="Togo">Togo</option>
                                    <option value="Tonga">Tonga</option>
                                    <option value="Trinidad and Tobago">Trinidad and Tobago</option>
                                    <option value="Tunisia">Tunisia</option>
                                    <option value="Turkey">Turkey</option>
                                    <option value="Turkmenistan">Turkmenistan</option>
                                    <option value="Tuvalu">Tuvalu</option>
                                    <option value="Uganda">Uganda</option>
                                    <option value="Ukraine">Ukraine</option>
                                    <option value="United Arab Emirates">United Arab Emirates</option>
                                    <option value="United Kingdom">United Kingdom</option>
                                    <option value="United States">United States</option>
                                    <option value="Uruguay">Uruguay</option>
                                    <option value="Uzbekistan">Uzbekistan</option>
                                    <option value="Vanuatu">Vanuatu</option>
                                    <option value="Venezuela">Venezuela</option>
                                    <option value="Vietnam">Vietnam</option>
                                    <option value="Yemen">Yemen</option>
                                    <option value="Zambia">Zambia</option>
                                    <option value="Zimbabwe">Zimbabwe</option>
                                </select>
                            </div>

                            <div>
                                <label for="addressLine1"
                                    class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    Address Line 1 <span class="text-red-500">*</span>
                                </label>
                                <input type="text" id="addressLine1" v-model="form.addressLine1" required
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                                    placeholder="Street address" />
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label for="addressLine2"
                                    class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    Address Line 2
                                </label>
                                <input type="text" id="addressLine2" v-model="form.addressLine2"
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                                    placeholder="Apartment, suite, etc. (optional)" />
                            </div>

                            <div>
                                <label for="addressLine3"
                                    class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    Address Line 3
                                </label>
                                <input type="text" id="addressLine3" v-model="form.addressLine3"
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                                    placeholder="Additional address info (optional)" />
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label for="city" class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    City <span class="text-red-500">*</span>
                                </label>
                                <input type="text" id="city" v-model="form.city" required
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                                    placeholder="City name" />
                            </div>

                            <div>
                                <label for="taxDistrict"
                                    class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    Tax District
                                </label>
                                <input type="text" id="taxDistrict" v-model="form.taxDistrict"
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                                    placeholder="Tax district" />
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label for="county"
                                    class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    County <span class="text-red-500">*</span>
                                </label>
                                <input type="text" id="county" v-model="form.county" required
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                                    placeholder="County name" />
                            </div>

                            <div>
                                <label for="state" class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    State <span class="text-red-500">*</span>
                                </label>
                                <input type="text" id="state" v-model="form.state" required
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                                    placeholder="State/Province" />
                            </div>

                            <div>
                                <label for="postalCode"
                                    class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    Postal Code <span class="text-red-500">*</span>
                                </label>
                                <input type="text" id="postalCode" v-model="form.postalCode" required
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                                    placeholder="ZIP/Postal code" />
                            </div>
                        </div>
                    </div>

                    <!-- Education Subsection -->
                    <div class="space-y-4">
                        <h3 class="text-lg font-marketing font-bold text-primary">Education</h3>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label for="degree"
                                    class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    Degree <span class="text-red-500">*</span>
                                </label>
                                <input type="text" id="degree" v-model="form.degree" required
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                                    placeholder="e.g., Bachelor of Arts" />
                            </div>

                            <div>
                                <label for="school"
                                    class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    School <span class="text-red-500">*</span>
                                </label>
                                <input type="text" id="school" v-model="form.school" required
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                                    placeholder="University or institution name" />
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label for="educationEndDate"
                                    class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    End Date <span class="text-red-500">*</span>
                                </label>
                                <input type="month" id="educationEndDate" v-model="form.educationEndDate" required
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
                            </div>

                            <div>
                                <label for="educationCountry"
                                    class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    Country <span class="text-red-500">*</span>
                                </label>
                                <select id="educationCountry" v-model="form.educationCountry" required
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans">
                                    <option value="">Select a country</option>
                                    <option value="Afghanistan">Afghanistan</option>
                                    <option value="Albania">Albania</option>
                                    <option value="Algeria">Algeria</option>
                                    <option value="Andorra">Andorra</option>
                                    <option value="Angola">Angola</option>
                                    <option value="Antigua and Barbuda">Antigua and Barbuda</option>
                                    <option value="Argentina">Argentina</option>
                                    <option value="Armenia">Armenia</option>
                                    <option value="Australia">Australia</option>
                                    <option value="Austria">Austria</option>
                                    <option value="Azerbaijan">Azerbaijan</option>
                                    <option value="Bahamas">Bahamas</option>
                                    <option value="Bahrain">Bahrain</option>
                                    <option value="Bangladesh">Bangladesh</option>
                                    <option value="Barbados">Barbados</option>
                                    <option value="Belarus">Belarus</option>
                                    <option value="Belgium">Belgium</option>
                                    <option value="Belize">Belize</option>
                                    <option value="Benin">Benin</option>
                                    <option value="Bhutan">Bhutan</option>
                                    <option value="Bolivia">Bolivia</option>
                                    <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
                                    <option value="Botswana">Botswana</option>
                                    <option value="Brazil">Brazil</option>
                                    <option value="Brunei">Brunei</option>
                                    <option value="Bulgaria">Bulgaria</option>
                                    <option value="Burkina Faso">Burkina Faso</option>
                                    <option value="Burundi">Burundi</option>
                                    <option value="Cabo Verde">Cabo Verde</option>
                                    <option value="Cambodia">Cambodia</option>
                                    <option value="Cameroon">Cameroon</option>
                                    <option value="Canada">Canada</option>
                                    <option value="Central African Republic">Central African Republic</option>
                                    <option value="Chad">Chad</option>
                                    <option value="Chile">Chile</option>
                                    <option value="China">China</option>
                                    <option value="Colombia">Colombia</option>
                                    <option value="Comoros">Comoros</option>
                                    <option value="Congo (Congo-Brazzaville)">Congo (Congo-Brazzaville)</option>
                                    <option value="Costa Rica">Costa Rica</option>
                                    <option value="Croatia">Croatia</option>
                                    <option value="Cuba">Cuba</option>
                                    <option value="Cyprus">Cyprus</option>
                                    <option value="Czech Republic">Czech Republic</option>
                                    <option value="Democratic Republic of the Congo">Democratic Republic of the Congo
                                    </option>
                                    <option value="Denmark">Denmark</option>
                                    <option value="Djibouti">Djibouti</option>
                                    <option value="Dominica">Dominica</option>
                                    <option value="Dominican Republic">Dominican Republic</option>
                                    <option value="Ecuador">Ecuador</option>
                                    <option value="Egypt">Egypt</option>
                                    <option value="El Salvador">El Salvador</option>
                                    <option value="Equatorial Guinea">Equatorial Guinea</option>
                                    <option value="Eritrea">Eritrea</option>
                                    <option value="Estonia">Estonia</option>
                                    <option value="Eswatini">Eswatini</option>
                                    <option value="Ethiopia">Ethiopia</option>
                                    <option value="Fiji">Fiji</option>
                                    <option value="Finland">Finland</option>
                                    <option value="France">France</option>
                                    <option value="Gabon">Gabon</option>
                                    <option value="Gambia">Gambia</option>
                                    <option value="Georgia">Georgia</option>
                                    <option value="Germany">Germany</option>
                                    <option value="Ghana">Ghana</option>
                                    <option value="Greece">Greece</option>
                                    <option value="Grenada">Grenada</option>
                                    <option value="Guatemala">Guatemala</option>
                                    <option value="Guinea">Guinea</option>
                                    <option value="Guinea-Bissau">Guinea-Bissau</option>
                                    <option value="Guyana">Guyana</option>
                                    <option value="Haiti">Haiti</option>
                                    <option value="Holy See">Holy See</option>
                                    <option value="Honduras">Honduras</option>
                                    <option value="Hungary">Hungary</option>
                                    <option value="Iceland">Iceland</option>
                                    <option value="India">India</option>
                                    <option value="Indonesia">Indonesia</option>
                                    <option value="Iran">Iran</option>
                                    <option value="Iraq">Iraq</option>
                                    <option value="Ireland">Ireland</option>
                                    <option value="Israel">Israel</option>
                                    <option value="Italy">Italy</option>
                                    <option value="Jamaica">Jamaica</option>
                                    <option value="Japan">Japan</option>
                                    <option value="Jordan">Jordan</option>
                                    <option value="Kazakhstan">Kazakhstan</option>
                                    <option value="Kenya">Kenya</option>
                                    <option value="Kiribati">Kiribati</option>
                                    <option value="Kuwait">Kuwait</option>
                                    <option value="Kyrgyzstan">Kyrgyzstan</option>
                                    <option value="Laos">Laos</option>
                                    <option value="Latvia">Latvia</option>
                                    <option value="Lebanon">Lebanon</option>
                                    <option value="Lesotho">Lesotho</option>
                                    <option value="Liberia">Liberia</option>
                                    <option value="Libya">Libya</option>
                                    <option value="Liechtenstein">Liechtenstein</option>
                                    <option value="Lithuania">Lithuania</option>
                                    <option value="Luxembourg">Luxembourg</option>
                                    <option value="Madagascar">Madagascar</option>
                                    <option value="Malawi">Malawi</option>
                                    <option value="Malaysia">Malaysia</option>
                                    <option value="Maldives">Maldives</option>
                                    <option value="Mali">Mali</option>
                                    <option value="Malta">Malta</option>
                                    <option value="Marshall Islands">Marshall Islands</option>
                                    <option value="Mauritania">Mauritania</option>
                                    <option value="Mauritius">Mauritius</option>
                                    <option value="Mexico">Mexico</option>
                                    <option value="Micronesia">Micronesia</option>
                                    <option value="Moldova">Moldova</option>
                                    <option value="Monaco">Monaco</option>
                                    <option value="Mongolia">Mongolia</option>
                                    <option value="Montenegro">Montenegro</option>
                                    <option value="Morocco">Morocco</option>
                                    <option value="Mozambique">Mozambique</option>
                                    <option value="Myanmar">Myanmar</option>
                                    <option value="Namibia">Namibia</option>
                                    <option value="Nauru">Nauru</option>
                                    <option value="Nepal">Nepal</option>
                                    <option value="Netherlands">Netherlands</option>
                                    <option value="New Zealand">New Zealand</option>
                                    <option value="Nicaragua">Nicaragua</option>
                                    <option value="Niger">Niger</option>
                                    <option value="Nigeria">Nigeria</option>
                                    <option value="North Korea">North Korea</option>
                                    <option value="North Macedonia">North Macedonia</option>
                                    <option value="Norway">Norway</option>
                                    <option value="Oman">Oman</option>
                                    <option value="Pakistan">Pakistan</option>
                                    <option value="Palau">Palau</option>
                                    <option value="Palestine State">Palestine State</option>
                                    <option value="Panama">Panama</option>
                                    <option value="Papua New Guinea">Papua New Guinea</option>
                                    <option value="Paraguay">Paraguay</option>
                                    <option value="Peru">Peru</option>
                                    <option value="Philippines">Philippines</option>
                                    <option value="Poland">Poland</option>
                                    <option value="Portugal">Portugal</option>
                                    <option value="Qatar">Qatar</option>
                                    <option value="Romania">Romania</option>
                                    <option value="Russia">Russia</option>
                                    <option value="Rwanda">Rwanda</option>
                                    <option value="Saint Kitts and Nevis">Saint Kitts and Nevis</option>
                                    <option value="Saint Lucia">Saint Lucia</option>
                                    <option value="Saint Vincent and the Grenadines">Saint Vincent and the Grenadines
                                    </option>
                                    <option value="Samoa">Samoa</option>
                                    <option value="San Marino">San Marino</option>
                                    <option value="Sao Tome and Principe">Sao Tome and Principe</option>
                                    <option value="Saudi Arabia">Saudi Arabia</option>
                                    <option value="Senegal">Senegal</option>
                                    <option value="Serbia">Serbia</option>
                                    <option value="Seychelles">Seychelles</option>
                                    <option value="Sierra Leone">Sierra Leone</option>
                                    <option value="Singapore">Singapore</option>
                                    <option value="Slovakia">Slovakia</option>
                                    <option value="Slovenia">Slovenia</option>
                                    <option value="Solomon Islands">Solomon Islands</option>
                                    <option value="Somalia">Somalia</option>
                                    <option value="South Africa">South Africa</option>
                                    <option value="South Korea">South Korea</option>
                                    <option value="South Sudan">South Sudan</option>
                                    <option value="Spain">Spain</option>
                                    <option value="Sri Lanka">Sri Lanka</option>
                                    <option value="Sudan">Sudan</option>
                                    <option value="Suriname">Suriname</option>
                                    <option value="Sweden">Sweden</option>
                                    <option value="Switzerland">Switzerland</option>
                                    <option value="Syria">Syria</option>
                                    <option value="Tajikistan">Tajikistan</option>
                                    <option value="Tanzania">Tanzania</option>
                                    <option value="Thailand">Thailand</option>
                                    <option value="Timor-Leste">Timor-Leste</option>
                                    <option value="Togo">Togo</option>
                                    <option value="Tonga">Tonga</option>
                                    <option value="Trinidad and Tobago">Trinidad and Tobago</option>
                                    <option value="Tunisia">Tunisia</option>
                                    <option value="Turkey">Turkey</option>
                                    <option value="Turkmenistan">Turkmenistan</option>
                                    <option value="Tuvalu">Tuvalu</option>
                                    <option value="Uganda">Uganda</option>
                                    <option value="Ukraine">Ukraine</option>
                                    <option value="United Arab Emirates">United Arab Emirates</option>
                                    <option value="United Kingdom">United Kingdom</option>
                                    <option value="United States">United States</option>
                                    <option value="Uruguay">Uruguay</option>
                                    <option value="Uzbekistan">Uzbekistan</option>
                                    <option value="Vanuatu">Vanuatu</option>
                                    <option value="Venezuela">Venezuela</option>
                                    <option value="Vietnam">Vietnam</option>
                                    <option value="Yemen">Yemen</option>
                                    <option value="Zambia">Zambia</option>
                                    <option value="Zimbabwe">Zimbabwe</option>
                                </select>
                            </div>

                            <div>
                                <label for="areaOfStudy"
                                    class="block text-sm font-marketing font-semibold text-primary mb-2">
                                    Area of Study <span class="text-red-500">*</span>
                                </label>
                                <input type="text" id="areaOfStudy" v-model="form.areaOfStudy" required
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                                    placeholder="Major or field of study" />
                            </div>
                        </div>
                    </div>

                    <!-- Application Questions Subsection -->
                    <div class="space-y-4">
                        <h3 class="text-lg font-marketing font-bold text-primary">Application Questions</h3>

                        <div class="space-y-4">
                            <div class="border border-gray-200 rounded-md p-4">
                                <label class="block text-sm font-marketing font-semibold text-primary mb-3">
                                    Are you at least 18 years of age? <span class="text-red-500">*</span>
                                </label>
                                <div class="flex gap-6">
                                    <label class="flex items-center">
                                        <input type="radio" v-model="form.isAtLeast18" :value="true" required
                                            class="w-4 h-4 text-accent border-gray-300 focus:ring-accent mr-2" />
                                        <span class="text-sm font-sans">Yes</span>
                                    </label>
                                    <label class="flex items-center">
                                        <input type="radio" v-model="form.isAtLeast18" :value="false" required
                                            class="w-4 h-4 text-accent border-gray-300 focus:ring-accent mr-2" />
                                        <span class="text-sm font-sans">No</span>
                                    </label>
                                </div>
                            </div>

                            <div class="border border-gray-200 rounded-md p-4">
                                <label class="block text-sm font-marketing font-semibold text-primary mb-3">
                                    Are you legally authorized to work in the United States? <span
                                        class="text-red-500">*</span>
                                </label>
                                <p class="text-xs text-gray-600 mb-3 font-sans">
                                    Note that if you are hired, you may be required to provide government-issued proof
                                    of your work authorization.
                                </p>
                                <div class="flex gap-6">
                                    <label class="flex items-center">
                                        <input type="radio" v-model="form.isAuthorizedToWork" :value="true" required
                                            class="w-4 h-4 text-accent border-gray-300 focus:ring-accent mr-2" />
                                        <span class="text-sm font-sans">Yes</span>
                                    </label>
                                    <label class="flex items-center">
                                        <input type="radio" v-model="form.isAuthorizedToWork" :value="false" required
                                            class="w-4 h-4 text-accent border-gray-300 focus:ring-accent mr-2" />
                                        <span class="text-sm font-sans">No</span>
                                    </label>
                                </div>
                            </div>

                            <div class="border border-gray-200 rounded-md p-4">
                                <label class="block text-sm font-marketing font-semibold text-primary mb-3">
                                    Will you now or in the future require sponsorship for employment-based visa status?
                                    <span class="text-red-500">*</span>
                                </label>
                                <div class="flex gap-6">
                                    <label class="flex items-center">
                                        <input type="radio" v-model="form.requiresSponsorship" :value="true" required
                                            class="w-4 h-4 text-accent border-gray-300 focus:ring-accent mr-2" />
                                        <span class="text-sm font-sans">Yes</span>
                                    </label>
                                    <label class="flex items-center">
                                        <input type="radio" v-model="form.requiresSponsorship" :value="false" required
                                            class="w-4 h-4 text-accent border-gray-300 focus:ring-accent mr-2" />
                                        <span class="text-sm font-sans">No</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label for="coverLetter" class="block text-sm font-marketing font-semibold text-primary mb-2">
                            Cover Letter Text (Optional)
                        </label>
                        <textarea id="coverLetter" v-model="form.coverLetter" rows="8"
                            class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans resize-none"
                            placeholder="Tell us why you are interested in this position and what makes you a great fit..."></textarea>
                    </div>

                    <div class="flex gap-4 pt-4">
                        <button type="submit" :disabled="submitting || !form.resumeFileUrl"
                            class="flex-1 bg-green-700 text-white px-8 py-3 rounded-md font-marketing font-semibold hover:bg-green-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed">
                            {{ submitting ? 'Submitting...' : 'Submit Application' }}
                        </button>
                        <a href="/careers"
                            class="px-8 py-3 bg-white text-green-700 border border-green-700 rounded-md font-marketing font-semibold hover:bg-green-50 transition-colors">
                            Cancel
                        </a>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { publicService } from '../services/public'
import { API_BASE_URL } from '../config.js'
import axios from 'axios'
import { validatePhoneNumber } from '../utils/phoneValidation'

const props = defineProps({
    careerId: {
        type: String,
        default: null
    }
})

const careerTitle = ref('')
const submitting = ref(false)
const submissionSuccess = ref(false)
const uploadingResume = ref(false)
const uploadingCoverLetter = ref(false)
const phoneError = ref(null)
const isDraggingResume = ref(false)
const isDraggingCoverLetter = ref(false)
const resumeInputRef = ref(null)
const coverLetterInputRef = ref(null)

const form = ref({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone: '',
    pronouns: '',
    pronounsCustom: '',
    country: '',
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    city: '',
    taxDistrict: '',
    county: '',
    state: '',
    postalCode: '',
    degree: '',
    school: '',
    educationEndDate: '',
    educationCountry: '',
    areaOfStudy: '',
    isAtLeast18: null,
    isAuthorizedToWork: null,
    requiresSponsorship: null,
    coverLetter: '',
    resumeFile: null,
    resumeFileName: '',
    resumeFileUrl: '',
    coverLetterFile: null,
    coverLetterFileName: '',
    coverLetterFileUrl: '',
    additionalLinks: [],
    yearsOfExperience: null,
    currentPosition: '',
    availabilityDate: '',
    additionalInfo: ''
})

const loadCareer = async () => {
    if (props.careerId) {
        try {
            const career = await publicService.getCareer(parseInt(props.careerId))
            careerTitle.value = career.title
        } catch (error) {
            console.error('Failed to load career:', error)
        }
    }
}

const triggerResumeInput = () => {
    resumeInputRef.value?.click()
}

const triggerCoverLetterInput = () => {
    coverLetterInputRef.value?.click()
}

const handleResumeDrop = async (event) => {
    isDraggingResume.value = false
    const file = event.dataTransfer.files[0]
    if (!file) return
    await uploadResume(file)
}

const handleCoverLetterDrop = async (event) => {
    isDraggingCoverLetter.value = false
    const file = event.dataTransfer.files[0]
    if (!file) return
    await uploadCoverLetter(file)
}

const handleResumeSelect = async (event) => {
    const file = event.target.files[0]
    if (!file) return
    await uploadResume(file)
}

const handleCoverLetterSelect = async (event) => {
    const file = event.target.files[0]
    if (!file) return
    await uploadCoverLetter(file)
}

const uploadResume = async (file) => {
    if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB')
        return
    }

    form.value.resumeFile = file
    form.value.resumeFileName = file.name
    uploadingResume.value = true

    try {
        const formData = new FormData()
        formData.append('file', file)

        const response = await axios.post(`${API_BASE_URL}/api/upload/resume`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })

        if (response.data && response.data.url) {
            form.value.resumeFileUrl = response.data.url
        } else {
            throw new Error('No URL returned from upload')
        }
    } catch (error) {
        console.error('Failed to upload resume:', error)
        alert('Failed to upload resume. Please try again.')
        form.value.resumeFile = null
        form.value.resumeFileName = ''
        form.value.resumeFileUrl = ''
    } finally {
        uploadingResume.value = false
    }
}

const uploadCoverLetter = async (file) => {
    if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB')
        return
    }

    form.value.coverLetterFile = file
    form.value.coverLetterFileName = file.name
    uploadingCoverLetter.value = true

    try {
        const formData = new FormData()
        formData.append('file', file)

        const response = await axios.post(`${API_BASE_URL}/api/upload/resume`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })

        if (response.data && response.data.url) {
            form.value.coverLetterFileUrl = response.data.url
        } else {
            throw new Error('No URL returned from upload')
        }
    } catch (error) {
        console.error('Failed to upload cover letter:', error)
        alert('Failed to upload cover letter. Please try again.')
        form.value.coverLetterFile = null
        form.value.coverLetterFileName = ''
        form.value.coverLetterFileUrl = ''
    } finally {
        uploadingCoverLetter.value = false
    }
}

const removeResume = () => {
    form.value.resumeFile = null
    form.value.resumeFileName = ''
    form.value.resumeFileUrl = ''
    if (resumeInputRef.value) {
        resumeInputRef.value.value = ''
    }
}

const removeCoverLetter = () => {
    form.value.coverLetterFile = null
    form.value.coverLetterFileName = ''
    form.value.coverLetterFileUrl = ''
    if (coverLetterInputRef.value) {
        coverLetterInputRef.value.value = ''
    }
}

const addLink = () => {
    form.value.additionalLinks.push({
        url: '',
        label: ''
    })
}

const removeLink = (index) => {
    form.value.additionalLinks.splice(index, 1)
}

const validatePhone = () => {
    if (!form.value.phone || form.value.phone.trim() === '') {
        phoneError.value = null
        return true
    }
    const validation = validatePhoneNumber(form.value.phone)
    phoneError.value = validation.error
    return validation.isValid
}

const handleSubmit = async () => {
    if (!form.value.resumeFileUrl) {
        alert('Please upload your resume/CV')
        return
    }

    if (!validatePhone()) {
        alert(phoneError.value || 'Please enter a valid phone number')
        return
    }

    // Validate required fields
    if (!form.value.firstName || !form.value.lastName || !form.value.email || !form.value.phone) {
        alert('Please fill in all required personal details')
        return
    }

    if (!form.value.country || !form.value.addressLine1 || !form.value.city ||
        !form.value.county || !form.value.state || !form.value.postalCode) {
        alert('Please fill in all required address fields')
        return
    }

    if (!form.value.degree || !form.value.school || !form.value.educationEndDate ||
        !form.value.educationCountry || !form.value.areaOfStudy) {
        alert('Please fill in all education fields')
        return
    }

    if (form.value.isAtLeast18 === null || form.value.isAuthorizedToWork === null ||
        form.value.requiresSponsorship === null) {
        alert('Please answer all application questions')
        return
    }

    submitting.value = true

    try {
        const applicationData = {
            career_id: props.careerId ? parseInt(props.careerId) : null,
            first_name: form.value.firstName,
            middle_name: form.value.middleName || null,
            last_name: form.value.lastName,
            email: form.value.email || null,
            phone: form.value.phone,
            pronouns: form.value.pronouns === 'self-describe' ? form.value.pronounsCustom : form.value.pronouns || null,
            country: form.value.country,
            address_line_1: form.value.addressLine1,
            address_line_2: form.value.addressLine2 || null,
            address_line_3: form.value.addressLine3 || null,
            city: form.value.city,
            tax_district: form.value.taxDistrict,
            county: form.value.county,
            state: form.value.state,
            postal_code: form.value.postalCode,
            degree: form.value.degree,
            school: form.value.school,
            education_end_date: form.value.educationEndDate,
            education_country: form.value.educationCountry,
            area_of_study: form.value.areaOfStudy,
            is_at_least_18: form.value.isAtLeast18,
            is_authorized_to_work: form.value.isAuthorizedToWork,
            requires_sponsorship: form.value.requiresSponsorship,
            cover_letter: form.value.coverLetter || null,
            resume_file_url: form.value.resumeFileUrl,
            cover_letter_file_url: form.value.coverLetterFileUrl || null,
            additional_links: form.value.additionalLinks.length > 0 ? JSON.stringify(form.value.additionalLinks.filter(link => link.url.trim())) : null,
            years_of_experience: form.value.yearsOfExperience || null,
            current_position: form.value.currentPosition || null,
            availability_date: form.value.availabilityDate || null,
            additional_info: form.value.additionalInfo || null
        }

        const response = await axios.post(`${API_BASE_URL}/api/career-applications`, applicationData)

        if (response.data) {
            submissionSuccess.value = true
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
    } catch (error) {
        console.error('Failed to submit application:', error)
        alert('Failed to submit application. Please try again.')
    } finally {
        submitting.value = false
    }
}

watch(() => props.careerId, () => {
    loadCareer()
}, { immediate: false })

onMounted(() => {
    loadCareer()
})
</script>

<style scoped>
.file-drop-zone {
    border: 2px dashed #DDC1A7;
    border-radius: 0.75rem;
    background: #FFFFFF;
    padding: 3rem 2rem;
    cursor: pointer;
    position: relative;
    transition: all 0.2s;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.file-drop-zone:hover {
    border-color: #B29E84;
}

.file-drop-zone.drag-over {
    border-color: #B29E84;
    background: #FAFAFA;
}

.file-drop-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
}

.file-selected-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
}
</style>
